export const easing = {
  linear: (t: number) => t,
  easeIn: (t: number) => t * t,
  easeOut: (t: number) => t * (2 - t),
  easeInOut: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  bounce: (t: number) => {
    const n1 = 7.5625, d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    else if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
    else if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
    else return n1 * (t -= 2.625 / d1) * t + 0.984375;
  },
  elastic: (t: number) => {
    if (t === 0 || t === 1) return t;
    return -(Math.pow(2, 10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI));
  },
  cubic: (t: number) => t * t * t,
  back: (t: number) => {
    const s = 1.70158;
    return t * t * ((s + 1) * t - s);
  },
};

export type EasingFunction = (t: number) => number;

export interface AnimationHandle {
  pause(): void;
  resume(): void;
  stop(): void;
  isRunning(): boolean;
  getProgress(): number;
}

export const animate = {
  flyTo(
    map: any,
    options: {
      center: [number, number];
      zoom?: number;
      rotation?: number;
      duration?: number;
      easing?: EasingFunction;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      if (map._maplibreMap) {
        map._maplibreMap.flyTo({
          center: options.center,
          zoom: options.zoom,
          bearing: options.rotation ? (options.rotation * 180) / Math.PI : undefined,
          duration: options.duration || 2000,
        });
        map._maplibreMap.once('moveend', () => resolve());
      } else {
        resolve();
      }
    });
  },

  panTo(map: any, center: [number, number], duration: number = 1000): Promise<void> {
    return new Promise((resolve) => {
      if (map._maplibreMap) {
        map._maplibreMap.panTo(center, { duration });
        map._maplibreMap.once('moveend', () => resolve());
      } else {
        resolve();
      }
    });
  },

  zoomTo(map: any, zoom: number, duration: number = 1000): Promise<void> {
    return new Promise((resolve) => {
      if (map._maplibreMap) {
        map._maplibreMap.zoomTo(zoom, { duration });
        map._maplibreMap.once('moveend', () => resolve());
      } else {
        resolve();
      }
    });
  },

  rotateTo(map: any, rotation: number, duration: number = 1000): Promise<void> {
    return new Promise((resolve) => {
      if (map._maplibreMap) {
        map._maplibreMap.rotateTo((rotation * 180) / Math.PI, { duration });
        map._maplibreMap.once('moveend', () => resolve());
      } else {
        resolve();
      }
    });
  },

  along(
    feature: any,
    path: [number, number][],
    options: {
      duration: number;
      repeat?: boolean;
      easing?: EasingFunction;
      onProgress?: (fraction: number, coordinate: [number, number]) => void;
      onComplete?: () => void;
      heading?: boolean;
    }
  ): AnimationHandle {
    let running = true;
    let paused = false;
    let progress = 0;
    let startTime = performance.now();
    let pauseTime = 0;
    const easingFn = options.easing || easing.linear;

    function tick(now: number) {
      if (!running) return;
      if (paused) {
        requestAnimationFrame(tick);
        return;
      }

      const elapsed = now - startTime;
      progress = Math.min(elapsed / options.duration, 1);
      const easedProgress = easingFn(progress);

      const totalDist = path.reduce((sum, p, i) => {
        if (i === 0) return 0;
        const dx = p[0] - path[i - 1][0];
        const dy = p[1] - path[i - 1][1];
        return sum + Math.sqrt(dx * dx + dy * dy);
      }, 0);

      let targetDist = easedProgress * totalDist;
      let accum = 0;
      let coord: [number, number] = path[0];

      for (let i = 1; i < path.length; i++) {
        const dx = path[i][0] - path[i - 1][0];
        const dy = path[i][1] - path[i - 1][1];
        const segLen = Math.sqrt(dx * dx + dy * dy);
        if (accum + segLen >= targetDist) {
          const f = (targetDist - accum) / segLen;
          coord = [path[i - 1][0] + dx * f, path[i - 1][1] + dy * f];
          break;
        }
        accum += segLen;
      }

      options.onProgress?.(progress, coord);

      if (progress >= 1) {
        if (options.repeat) {
          startTime = now;
          requestAnimationFrame(tick);
        } else {
          running = false;
          options.onComplete?.();
        }
      } else {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);

    return {
      pause() {
        paused = true;
        pauseTime = performance.now();
      },
      resume() {
        if (paused) {
          startTime += performance.now() - pauseTime;
          paused = false;
        }
      },
      stop() {
        running = false;
      },
      isRunning() {
        return running;
      },
      getProgress() {
        return progress;
      },
    };
  },
};
